import { v4 as uuidv4 } from 'uuid';
import { AttributionCategory, AdjustmentType } from '@frontend/pricing-search';
import validate from '../../../../common/utils/validator';
import { validators } from './constants';

export const checkCardsType = (type) => {
  const isAdjustment = [
    'LockExtensionAdjustment',
    'ReLockFeeAdjustment',
    'CustomPriceAdjustment',
  ].includes(type);
  return isAdjustment ? 'adjustments' : 'concessions';
};

export const validateRequiredInputs = (rows, type) => {
  const validator = validators[type];
  const { hasError, errorDetails } = validate({ [type]: rows }, [validator]);
  return { hasError, errors: errorDetails[type] };
};

export const parseDecimals = (valueToParse, decimals) => {
  let toNumber;

  if (valueToParse || valueToParse === 0) {
    toNumber = Number(valueToParse);
  }

  if (typeof toNumber === 'number' && !Number.isNaN(toNumber)) {
    return toNumber.toFixed(decimals);
  }

  return valueToParse;
};

export const getAbbreviationByCategory = (
  guid: string,
  attrCategories: AttributionCategory[],
) => {
  if (!attrCategories?.length || guid === null) return null;
  const category = attrCategories?.find((cat) => cat.guid === guid);
  if (category) {
    return category.abbreviation;
  }
  return '';
};

export const extractApprovedReason = (
  approvedReason,
  paidByGuid,
  adjustmentReasonGuid,
) => {
  if (!approvedReason || !approvedReason.includes('-')) return approvedReason;
  const approvedReasonArr = approvedReason.split('-');
  let reason;
  if (paidByGuid || adjustmentReasonGuid) {
    reason = approvedReasonArr.pop();
  } else {
    reason = approvedReasonArr[0];
  }
  return reason;
};

const constructTempApprovedReason = (
  approvedReason: string,
  paidByGuid: string,
  adjustmentReasonGuid: string,
  allCategories,
  isCorrection = false,
) => {
  const paidByAbbr = getAbbreviationByCategory(
    paidByGuid,
    allCategories?.paidByCategories,
  );
  const adjustmentReasonAbbr = getAbbreviationByCategory(
    adjustmentReasonGuid,
    allCategories?.adjustmentReasonCategories,
  );

  const tempStringHolder = isCorrection
    ? extractApprovedReason(approvedReason, paidByGuid, adjustmentReasonGuid)
    : approvedReason;
  return `${paidByAbbr === null ? '' : paidByAbbr}${paidByAbbr ? '-' : ''}${
    adjustmentReasonAbbr === null ? '' : adjustmentReasonAbbr
  }${adjustmentReasonAbbr ? '-' : ''}${tempStringHolder}`;
};

export const mapTempApprovedReasonToCorrectAbbreviation = (
  approvedReason: string,
  paidByGuid: string,
  adjustmentReasonGuid: string,
  allAttrCategories,
) => {
  if (!approvedReason) return approvedReason;

  return constructTempApprovedReason(
    approvedReason,
    paidByGuid,
    adjustmentReasonGuid,
    allAttrCategories,
    true,
  );
};

export const setAbbreviatedCategoriesToApprovedReason = (
  cards,
  allCategories,
) => {
  return cards.map((card) => {
    if (
      card.adjustmentType === AdjustmentType.BranchPriceConcession ||
      card.adjustmentType === AdjustmentType.CorporatePriceConcession
    ) {
      // if no paidByGuid or adjustmentReasonGuid, return card as is
      if (card?.paidByGuid === null && card?.adjustmentReasonGuid === null) {
        card.tempApprovedReason = card.approvedReason;
        return card;
      }

      // Strip any existing abbreviation prefixes from approvedReason
      // so the raw reason is clean for validation (no false-positive dash errors)
      const cleanApprovedReason = extractApprovedReason(
        card?.approvedReason,
        card?.paidByGuid,
        card?.adjustmentReasonGuid,
      );
      const paidByAbbr = getAbbreviationByCategory(
        card.paidByGuid,
        allCategories?.paidByCategories,
      );
      const adjustmentReasonAbbr = getAbbreviationByCategory(
        card.adjustmentReasonGuid,
        allCategories?.adjustmentReasonCategories,
      );
      return {
        ...card,
        approvedReason: cleanApprovedReason,
        tempApprovedReason: `${paidByAbbr === null ? '' : paidByAbbr}${
          paidByAbbr ? '-' : ''
        }${adjustmentReasonAbbr === null ? '' : adjustmentReasonAbbr}${
          adjustmentReasonAbbr ? '-' : ''
        }${cleanApprovedReason}`,
        adjustmentReasonGuid: card?.adjustmentReasonGuid,
        paidByGuid: card?.paidByGuid,
      };
    }
    return card;
  });
};

export const normalizeCardsData = (cards) => {
  const result = cards.map((card) => ({
    ...card,
    price: parseFloat(card?.price) || null,
    rate: parseFloat(card?.rate) || null,
    id: card.id || uuidv4(),
  }));
  return result;
};

export const normalizeAdjustmentsData = (adjs) => {
  const result = adjs.map((adj) => ({
    ...adj,
    price: Number(adj.price).toFixed(3),
    rate: Number(adj.rate).toFixed(3),
  }));
  return result;
};
